import { c as _c } from "react/compiler-runtime";
// Настройки персональной ленты товаров главной (п.16 правок 27.10.2026):
// переключатель «вкл/выкл» и веса сигналов. Живёт на странице «Выдача» над
// списком блоков — блок «Персональная лента» в выдаче отмечает место ленты, а
// здесь настраивается, КАК она ранжирует товары.
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type PersonalFeedSettings } from "@/api";
const DEFAULTS: PersonalFeedSettings = {
  enabled: true,
  weights: {
    purchase: 10,
    cart: 5,
    favorite: 4,
    shopFollow: 3,
    view: 1.5,
    impression: -0.15
  },
  halfLifeDays: 21,
  explorationShare: 0.15,
  maxSameShopInRow: 2
};
const WEIGHT_KEYS = ["purchase", "cart", "favorite", "shopFollow", "view", "impression"] as const;
const key = ["feed", "personal-settings"] as const;
export function PersonalFeedSettingsCard() {
  const $ = _c(130);
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: key,
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const query = useQuery(t0);
  const [draft, setDraft] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  let t1;
  if ($[1] !== queryClient) {
    t1 = {
      mutationFn: _temp2,
      onSuccess: data => {
        queryClient.setQueryData(key, data);
        setDraft(null);
        setSavedAt(Date.now());
      }
    };
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const save = useMutation(t1);
  if (query.isPending) {
    let t2;
    if ($[3] !== t) {
      t2 = t("feed.personalFeed.loading");
      $[3] = t;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== t2) {
      t3 = <p className="text-sm text-muted-foreground">{t2}</p>;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  const current = draft ?? query.data ?? DEFAULTS;
  let t2;
  if ($[7] !== current) {
    t2 = patch => {
      setSavedAt(null);
      setDraft({
        ...current,
        ...patch,
        weights: {
          ...current.weights,
          ...(patch.weights ?? {})
        }
      });
    };
    $[7] = current;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const edit = t2;
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <UserRound className="size-4" />;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t) {
    t4 = t("feed.personalFeed.title");
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <h2 className="flex items-center gap-2 text-base font-semibold">{t3} {t4}</h2>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("feed.personalFeed.description");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6) {
    t7 = <p className="max-w-2xl text-sm text-muted-foreground">{t6}</p>;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t5 || $[19] !== t7) {
    t8 = <div className="space-y-1">{t5}{t7}</div>;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== current || $[22] !== save) {
    t9 = e => {
      const next_0 = {
        ...current,
        enabled: e.target.checked
      };
      setDraft(next_0);
      save.mutate(next_0);
    };
    $[21] = current;
    $[22] = save;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== current.enabled || $[25] !== t9) {
    t10 = <input type="checkbox" data-testid="personal-feed-enabled" className="size-4" checked={current.enabled} onChange={t9} />;
    $[24] = current.enabled;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t) {
    t11 = t("feed.personalFeed.enabled");
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = <label className="flex shrink-0 items-center gap-2 text-sm font-medium">{t10}{t11}</label>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12 || $[33] !== t8) {
    t13 = <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">{t8}{t12}</div>;
    $[32] = t12;
    $[33] = t8;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t) {
    t14 = t("feed.personalFeed.weightsTitle");
    $[35] = t;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t14) {
    t15 = <h3 className="text-sm font-medium">{t14}</h3>;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t) {
    t16 = t("feed.personalFeed.weightsHint");
    $[39] = t;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t16) {
    t17 = <p className="text-xs text-muted-foreground">{t16}</p>;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t15 || $[44] !== t17) {
    t18 = <div>{t15}{t17}</div>;
    $[43] = t15;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== current.weights || $[47] !== edit || $[48] !== t) {
    t19 = WEIGHT_KEYS.map(k => <div key={k} className="space-y-1"><Label htmlFor={`pf-${k}`} className="text-xs">{t(`feed.personalFeed.${k}`)}</Label><NumberField id={`pf-${k}`} value={current.weights[k]} onCommit={v => edit({
        weights: {
          ...current.weights,
          [k]: v
        }
      })} /></div>);
    $[46] = current.weights;
    $[47] = edit;
    $[48] = t;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t19) {
    t20 = <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">{t19}</div>;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t18 || $[53] !== t20) {
    t21 = <div className="space-y-2">{t18}{t20}</div>;
    $[52] = t18;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t) {
    t22 = t("feed.personalFeed.halfLifeDays");
    $[55] = t;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== t22) {
    t23 = <Label htmlFor="pf-half" className="text-xs">{t22}</Label>;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== edit) {
    t24 = v_0 => edit({
      halfLifeDays: v_0
    });
    $[59] = edit;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== current.halfLifeDays || $[62] !== t24) {
    t25 = <NumberField id="pf-half" value={current.halfLifeDays} onCommit={t24} />;
    $[61] = current.halfLifeDays;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t23 || $[65] !== t25) {
    t26 = <div className="space-y-1">{t23}{t25}</div>;
    $[64] = t23;
    $[65] = t25;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== t) {
    t27 = t("feed.personalFeed.explorationShare");
    $[67] = t;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t27) {
    t28 = <Label htmlFor="pf-explore" className="text-xs">{t27}</Label>;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== current.explorationShare) {
    t29 = Math.round(current.explorationShare * 100);
    $[71] = current.explorationShare;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== edit) {
    t30 = v_1 => edit({
      explorationShare: v_1 / 100
    });
    $[73] = edit;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t29 || $[76] !== t30) {
    t31 = <NumberField id="pf-explore" value={t29} onCommit={t30} />;
    $[75] = t29;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t28 || $[79] !== t31) {
    t32 = <div className="space-y-1">{t28}{t31}</div>;
    $[78] = t28;
    $[79] = t31;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== t) {
    t33 = t("feed.personalFeed.maxSameShopInRow");
    $[81] = t;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== t33) {
    t34 = <Label htmlFor="pf-shop" className="text-xs">{t33}</Label>;
    $[83] = t33;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] !== edit) {
    t35 = v_2 => edit({
      maxSameShopInRow: Math.round(v_2)
    });
    $[85] = edit;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== current.maxSameShopInRow || $[88] !== t35) {
    t36 = <NumberField id="pf-shop" value={current.maxSameShopInRow} onCommit={t35} />;
    $[87] = current.maxSameShopInRow;
    $[88] = t35;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[90] !== t34 || $[91] !== t36) {
    t37 = <div className="space-y-1">{t34}{t36}</div>;
    $[90] = t34;
    $[91] = t36;
    $[92] = t37;
  } else {
    t37 = $[92];
  }
  let t38;
  if ($[93] !== t26 || $[94] !== t32 || $[95] !== t37) {
    t38 = <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{t26}{t32}{t37}</div>;
    $[93] = t26;
    $[94] = t32;
    $[95] = t37;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  let t39;
  if ($[97] !== save.error) {
    t39 = save.error && <p className="text-sm text-destructive">{(save.error as Error).message}</p>;
    $[97] = save.error;
    $[98] = t39;
  } else {
    t39 = $[98];
  }
  let t40;
  if ($[99] !== current || $[100] !== save) {
    t40 = () => save.mutate(current);
    $[99] = current;
    $[100] = save;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  const t41 = save.isPending || draft == null;
  let t42;
  if ($[102] !== t) {
    t42 = t("feed.personalFeed.save");
    $[102] = t;
    $[103] = t42;
  } else {
    t42 = $[103];
  }
  let t43;
  if ($[104] !== t40 || $[105] !== t41 || $[106] !== t42) {
    t43 = <Button onClick={t40} disabled={t41}>{t42}</Button>;
    $[104] = t40;
    $[105] = t41;
    $[106] = t42;
    $[107] = t43;
  } else {
    t43 = $[107];
  }
  let t44;
  if ($[108] !== current.enabled || $[109] !== edit) {
    t44 = () => edit({
      ...DEFAULTS,
      enabled: current.enabled
    });
    $[108] = current.enabled;
    $[109] = edit;
    $[110] = t44;
  } else {
    t44 = $[110];
  }
  const t45 = save.isPending;
  let t46;
  if ($[111] !== t) {
    t46 = t("feed.personalFeed.reset");
    $[111] = t;
    $[112] = t46;
  } else {
    t46 = $[112];
  }
  let t47;
  if ($[113] !== save.isPending || $[114] !== t44 || $[115] !== t46) {
    t47 = <Button variant="outline" onClick={t44} disabled={t45}>{t46}</Button>;
    $[113] = save.isPending;
    $[114] = t44;
    $[115] = t46;
    $[116] = t47;
  } else {
    t47 = $[116];
  }
  let t48;
  if ($[117] !== savedAt || $[118] !== t) {
    t48 = savedAt && <span className="text-xs text-muted-foreground">{t("feed.personalFeed.saved")}</span>;
    $[117] = savedAt;
    $[118] = t;
    $[119] = t48;
  } else {
    t48 = $[119];
  }
  let t49;
  if ($[120] !== t43 || $[121] !== t47 || $[122] !== t48) {
    t49 = <div className="flex flex-wrap items-center gap-2">{t43}{t47}{t48}</div>;
    $[120] = t43;
    $[121] = t47;
    $[122] = t48;
    $[123] = t49;
  } else {
    t49 = $[123];
  }
  let t50;
  if ($[124] !== t13 || $[125] !== t21 || $[126] !== t38 || $[127] !== t39 || $[128] !== t49) {
    t50 = <section className="space-y-4 rounded-lg border bg-card p-4" data-testid="personal-feed-settings">{t13}{t21}{t38}{t39}{t49}</section>;
    $[124] = t13;
    $[125] = t21;
    $[126] = t38;
    $[127] = t39;
    $[128] = t49;
    $[129] = t50;
  } else {
    t50 = $[129];
  }
  return t50;
}

/**
 * Числовое поле: текст правится свободно («1,» по дороге к «1,5»), число
 * уходит в черновик на каждом вводе, если строка уже читается как число.
 */
function _temp2(next) {
  return api.feed.updatePersonalSettings(next);
}
function _temp() {
  return api.feed.personalSettings();
}
function NumberField(t0) {
  const $ = _c(10);
  const {
    id,
    value,
    onCommit
  } = t0;
  const [text, setText] = useState(null);
  let t1;
  if ($[0] !== text || $[1] !== value) {
    t1 = text ?? String(value).replace(".", ",");
    $[0] = text;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onCommit) {
    t2 = e => {
      setText(e.target.value);
      const n = Number(e.target.value.replace(",", ".").trim());
      if (e.target.value.trim() !== "" && Number.isFinite(n)) {
        onCommit(n);
      }
    };
    $[3] = onCommit;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setText(null);
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== id || $[7] !== t1 || $[8] !== t2) {
    t4 = <Input id={id} inputMode="decimal" value={t1} onChange={t2} onBlur={t3} />;
    $[6] = id;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}